/**
 * SheetTemplateTitleblockPanel — the "Titleblock" sub-view of a Drawing Sheet Template's own
 * editor (TemplatesWorkspace.jsx's EditorView, type==='ds'). Replaces the earlier standalone
 * "Titleblocks" tab of nebulous, self-named "profiles" — that abstraction was confusing because
 * it wasn't clear what a profile applied to. The real model: the issuance block's position is a
 * property of the sheet's PHYSICAL SIZE (every sheet template stamped from the same Revit
 * titleblock family shares one corner), while note stickers (Site Plan Notes, etc.) are scoped to
 * specific sheet TEMPLATES. So the template screen is the host for both: it loads a real, existing
 * PDF of THIS template (any project) as the calibration background, silently keys the issuance
 * block's position to the detected page size (auto-named — the user never types a profile name),
 * and lets you assign/position note stickers for this template directly on the same canvas. One
 * screen, one Save. See docs/TITLEBLOCK_ISSUANCE.md.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { loadPdfjs } from '../lib/pdfjs.js'
import { visualSize } from '../lib/pdfFrame.js'
import { computeIssuancePrimitives, drawIssuanceCanvas, makeDefaultLayout, pickProfileForFrame } from '../lib/issuanceLayout.js'
import {
  listTitleblockProfiles, saveTitleblockProfile, updateTitleblockProfile,
  listNoteStickers, saveNoteSticker, updateNoteSticker, deleteNoteSticker,
} from '../services/sheetIssuance.js'
import { getActiveProjects } from '../services/commands.js'
import { getProjectSheetsWithLatestPdf, getSampleSheetForTemplate } from '../services/sheets.js'
import { confirmDialog } from './Dialog.jsx'
import siteplanNotesStarterHtml from '../starters/siteplanNotes.html?raw'
import siteplanNotesStarterCss from '../starters/siteplanNotes.css?raw'

const SAMPLE = { project_no: '26-02', issue_date: '07/13/2026', issue_title: 'DAPC', zoning_no: '-', building_no: '-' }
const getFirst = v => Array.isArray(v) ? (v[0] ?? '') : (v ?? '')
const DEFAULT_NOTE_POS = { x: 0.72, y: 0.08 }

const s = {
  label: { display: 'block', fontSize: 11, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input: { display: 'block', width: '100%', padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontSize: 13, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', outline: 'none', boxSizing: 'border-box' },
  btnPrimary: { padding: '6px 14px', background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', flexShrink: 0 },
  btnSecondary: { padding: '5px 10px', background: 'transparent', color: 'var(--aj-text)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontSize: 12, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', flexShrink: 0 },
  link: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--aj-accent-blue, #1a6fa8)', fontSize: 12, fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' },
}

// pdf.js page → our frame { w, h, rotation, ox, oy } (unrotated box + /Rotate), and visual size.
function frameFromPdfjsPage(page) {
  const v = page.view || [0, 0, 612, 792]           // [x0,y0,x1,y1] unrotated
  const w = v[2] - v[0], h = v[3] - v[1]
  const rotation = ((Number(page.rotate) || 0) % 360 + 360) % 360
  const frame = { w, h, rotation, ox: v[0], oy: v[1] }
  return frame
}

const HANDLE = 12  // px, resize handle hit/draw size
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

export function SheetTemplateTitleblockPanel({ templateId, templateName, addToast }) {
  const [rect, setRect] = useState(makeDefaultLayout().rect)
  const [frame, setFrame] = useState(null)
  const [vis, setVis] = useState(null)
  const [pageBitmap, setPageBitmap] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [noSample, setNoSample] = useState(false)
  const [manualPickerOpen, setManualPickerOpen] = useState(false)

  // Manual fallback: pick any real project sheet (used only when no auto-sample exists, or on request).
  const [projects, setProjects] = useState(null)
  const [projectId, setProjectId] = useState('')
  const [sheets, setSheets] = useState(null)
  const [sheetId, setSheetId] = useState('')

  // All sizes + all note blocks (loaded once; this template's own state derives from them).
  const [profiles, setProfiles] = useState(null)
  const [matchedProfileId, setMatchedProfileId] = useState(null)
  const [notes, setNotes] = useState(null)
  const [assigned, setAssigned] = useState(new Set())   // noteId set — assigned to THIS template (local, editable)
  const [notePos, setNotePos] = useState({})            // noteId -> { x, y } visual-fraction top-left, this size
  const [editingNote, setEditingNote] = useState(null)  // null | note | 'new'

  const reload = useCallback(() => {
    listTitleblockProfiles().then(setProfiles).catch(() => setProfiles([]))
    listNoteStickers().then(setNotes).catch(() => setNotes([]))
  }, [])
  useEffect(() => { reload() }, [reload])

  // Auto-load a real sample sheet of THIS exact template, across any project.
  useEffect(() => {
    let cancelled = false
    setLoading(true); setNoSample(false); setErr('')
    getSampleSheetForTemplate(templateName).then(res => {
      if (cancelled) return
      if (res && res.pdfUrl) loadSheetPdf(res.pdfUrl)
      else { setLoading(false); setNoSample(true); setManualPickerOpen(true) }
    }).catch(() => { if (!cancelled) { setLoading(false); setNoSample(true); setManualPickerOpen(true) } })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateName])

  useEffect(() => { if (manualPickerOpen && projects === null) getActiveProjects().then(recs => setProjects(recs || [])).catch(() => setProjects([])) }, [manualPickerOpen, projects])
  useEffect(() => {
    if (!projectId) { setSheets(null); return }
    setSheets(null); setSheetId('')
    getProjectSheetsWithLatestPdf(projectId).then(list => setSheets((list || []).filter(x => x.pdfUrl))).catch(() => setSheets([]))
  }, [projectId])

  // Resolve which existing size profile (if any) matches the detected page size.
  useEffect(() => {
    if (!frame || !profiles) return
    const match = pickProfileForFrame(profiles, frame, visualSize)
    if (match) { setMatchedProfileId(match.id); setRect(match.layout?.rect || makeDefaultLayout().rect) }
    else { setMatchedProfileId(null); setRect(makeDefaultLayout().rect) }
  }, [frame, profiles])

  // This template's assigned notes, derived from server state each time notes reload.
  useEffect(() => {
    if (!notes) return
    setAssigned(new Set(notes.filter(n => (n.target_sheet_templates || []).includes(templateId)).map(n => n.id)))
  }, [notes, templateId])

  // Note positions: hydrate from the matched profile's stored position, default for newly-assigned.
  useEffect(() => {
    if (!notes) return
    setNotePos(prev => {
      const next = { ...prev }
      for (const n of notes) {
        if (assigned.has(n.id) && !next[n.id]) {
          const stored = matchedProfileId ? (n.positions || {})[matchedProfileId] : null
          next[n.id] = (stored && Number.isFinite(stored.x)) ? { x: stored.x, y: stored.y } : { ...DEFAULT_NOTE_POS }
        }
        if (!assigned.has(n.id) && next[n.id]) delete next[n.id]
      }
      return next
    })
  }, [notes, assigned, matchedProfileId])

  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const vpRef = useRef({ x: 0, y: 0, z: 1 })
  const dragRef = useRef(null)

  const contentToScreen = (cx, cy) => { const v = vpRef.current; return [cx * v.z + v.x, cy * v.z + v.y] }
  const screenToContent = (sx, sy) => { const v = vpRef.current; return [(sx - v.x) / v.z, (sy - v.y) / v.z] }

  const visibleNotes = (notes || []).filter(n => assigned.has(n.id) && notePos[n.id])

  const redraw = useCallback(() => {
    const cv = canvasRef.current, cont = containerRef.current
    if (!cv || !cont) return
    const dpr = window.devicePixelRatio || 1
    const cw = cont.clientWidth, ch = cont.clientHeight
    if (cv.width !== Math.floor(cw * dpr) || cv.height !== Math.floor(ch * dpr)) {
      cv.width = Math.floor(cw * dpr); cv.height = Math.floor(ch * dpr); cv.style.width = cw + 'px'; cv.style.height = ch + 'px'
    }
    const g = cv.getContext('2d')
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
    g.clearRect(0, 0, cw, ch)
    if (!pageBitmap) return
    const v = vpRef.current
    g.save(); g.translate(v.x, v.y); g.scale(v.z, v.z)
    g.drawImage(pageBitmap.canvas, 0, 0)
    const layout = { ...makeDefaultLayout(), rect }
    const prims = computeIssuancePrimitives(layout, SAMPLE)
    drawIssuanceCanvas(g, prims, { ox: 0, oy: 0, w: pageBitmap.w, h: pageBitmap.h })
    g.restore()
    const [sx, sy] = contentToScreen(rect.x * pageBitmap.w, rect.y * pageBitmap.h)
    const sw = rect.w * pageBitmap.w * v.z, sh = rect.h * pageBitmap.h * v.z
    g.strokeStyle = '#1D9E75'; g.lineWidth = 1.5; g.setLineDash([5, 4]); g.strokeRect(sx, sy, sw, sh); g.setLineDash([])
    g.fillStyle = '#1D9E75'; g.fillRect(sx + sw - HANDLE, sy + sh - HANDLE, HANDLE, HANDLE)

    for (const note of visibleNotes) {
      const p = notePos[note.id]
      const wF = vis && vis.vw ? ((Number(note.real_width_in) || 4.5) * 72) / vis.vw : 0.12
      const hF = 0.3
      const [nx, ny] = contentToScreen(p.x * pageBitmap.w, p.y * pageBitmap.h)
      const nw = wF * pageBitmap.w * v.z, nh = hF * pageBitmap.h * v.z
      g.fillStyle = 'rgba(26,111,168,0.10)'; g.fillRect(nx, ny, nw, nh)
      g.strokeStyle = '#1a6fa8'; g.lineWidth = 1.5; g.setLineDash([5, 4]); g.strokeRect(nx, ny, nw, nh); g.setLineDash([])
      g.fillStyle = '#1a6fa8'; g.font = '600 11px "DM Sans", sans-serif'; g.textBaseline = 'top'
      g.fillText('▚ ' + (note.name || 'Note'), nx + 4, ny + 3)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageBitmap, rect, visibleNotes, notePos, vis])

  useEffect(() => { redraw() }, [redraw])

  function fitView() {
    const cont = containerRef.current
    if (!cont || !pageBitmap) return
    const cw = cont.clientWidth, ch = cont.clientHeight
    const z = Math.min(cw / pageBitmap.w, ch / pageBitmap.h) * 0.96
    vpRef.current = { z, x: (cw - pageBitmap.w * z) / 2, y: (ch - pageBitmap.h * z) / 2 }
    redraw()
  }

  useEffect(() => { if (pageBitmap) fitView() }, [pageBitmap]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const cont = containerRef.current
    if (!cont || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => redraw())
    ro.observe(cont)
    return () => ro.disconnect()
  }, [redraw])

  useEffect(() => {
    const cv = canvasRef.current
    if (!cv) return
    function onWheel(e) {
      if (!pageBitmap) return
      e.preventDefault()
      const r = cv.getBoundingClientRect()
      const mx = e.clientX - r.left, my = e.clientY - r.top
      const v = vpRef.current
      const [cx, cy] = [(mx - v.x) / v.z, (my - v.y) / v.z]
      const nz = clamp(v.z * Math.exp(-e.deltaY * 0.0015), 0.05, 40)
      vpRef.current = { z: nz, x: mx - cx * nz, y: my - cy * nz }
      redraw()
    }
    cv.addEventListener('wheel', onWheel, { passive: false })
    return () => cv.removeEventListener('wheel', onWheel)
  }, [pageBitmap, redraw])

  async function loadSheetPdf(pdfUrl) {
    if (!pdfUrl) return
    setLoading(true); setErr('')
    try {
      const pdfjs = await loadPdfjs()
      const token = await window.__clerkGetToken()
      const resp = await fetch('/api/pdf-proxy?url=' + encodeURIComponent(pdfUrl), { headers: { Authorization: 'Bearer ' + token } })
      if (!resp.ok) throw new Error('proxy ' + resp.status)
      const buf = await resp.arrayBuffer()
      const doc = await pdfjs.getDocument({ data: buf }).promise
      const page = await doc.getPage(1)
      const fr = frameFromPdfjsPage(page)
      const vv = visualSize(fr)
      const vp1 = page.getViewport({ scale: 1 })
      const scale = clamp(2600 / vp1.width, 0.5, 3)
      const vp = page.getViewport({ scale })
      const off = document.createElement('canvas')
      off.width = Math.round(vp.width); off.height = Math.round(vp.height)
      await page.render({ canvasContext: off.getContext('2d'), viewport: vp }).promise
      setFrame(fr); setVis(vv); setPageBitmap({ canvas: off, w: off.width, h: off.height })
    } catch (e) {
      setErr('Could not load that sheet PDF. Try another.')
    } finally { setLoading(false) }
  }

  function onPickSheet(id) {
    setSheetId(id)
    const sh = (sheets || []).find(x => x.id === id)
    if (sh) loadSheetPdf(sh.pdfUrl)
  }

  function evtScreen(e) { const r = canvasRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top] }

  function onPointerDown(e) {
    if (!pageBitmap) return
    const [sx, sy] = evtScreen(e)
    const v = vpRef.current
    const [hsx, hsy] = contentToScreen((rect.x + rect.w) * pageBitmap.w, (rect.y + rect.h) * pageBitmap.h)
    const onHandle = Math.abs(sx - hsx) <= HANDLE + 4 && Math.abs(sy - hsy) <= HANDLE + 4
    const [cx, cy] = screenToContent(sx, sy)
    const fx = cx / pageBitmap.w, fy = cy / pageBitmap.h
    if (onHandle) { dragRef.current = { mode: 'resize' }; canvasRef.current.setPointerCapture(e.pointerId); return }
    for (let i = visibleNotes.length - 1; i >= 0; i--) {
      const note = visibleNotes[i], p = notePos[note.id]; if (!p) continue
      const wF = vis && vis.vw ? ((Number(note.real_width_in) || 4.5) * 72) / vis.vw : 0.12, hF = 0.3
      if (fx >= p.x && fx <= p.x + wF && fy >= p.y && fy <= p.y + hF) {
        dragRef.current = { mode: 'note', noteId: note.id, fx, fy, start: { ...p } }
        canvasRef.current.setPointerCapture(e.pointerId); return
      }
    }
    const inside = fx >= rect.x && fx <= rect.x + rect.w && fy >= rect.y && fy <= rect.y + rect.h
    if (inside) dragRef.current = { mode: 'move', fx, fy, start: { ...rect } }
    else dragRef.current = { mode: 'pan', sx, sy, vx: v.x, vy: v.y }
    canvasRef.current.setPointerCapture(e.pointerId)
  }
  function onPointerMove(e) {
    const d = dragRef.current
    if (!d || !pageBitmap) return
    const [sx, sy] = evtScreen(e)
    if (d.mode === 'pan') {
      vpRef.current = { ...vpRef.current, x: d.vx + (sx - d.sx), y: d.vy + (sy - d.sy) }
      redraw(); return
    }
    const [cx, cy] = screenToContent(sx, sy)
    const fx = cx / pageBitmap.w, fy = cy / pageBitmap.h
    if (d.mode === 'note') {
      const nx = clamp(d.start.x + (fx - d.fx), 0, 1)
      const ny = clamp(d.start.y + (fy - d.fy), 0, 1)
      setNotePos(m => ({ ...m, [d.noteId]: { x: nx, y: ny } }))
    } else if (d.mode === 'move') {
      const nx = clamp(d.start.x + (fx - d.fx), 0, 1 - d.start.w)
      const ny = clamp(d.start.y + (fy - d.fy), 0, 1 - d.start.h)
      setRect(r => ({ ...r, x: nx, y: ny }))
    } else {
      setRect(r => ({ ...r, w: clamp(fx - r.x, 0.02, 1 - r.x), h: clamp(fy - r.y, 0.02, 1 - r.y) }))
    }
  }
  function onPointerUp(e) { dragRef.current = null; try { canvasRef.current.releasePointerCapture(e.pointerId) } catch {} }

  function toggleAssigned(noteId) {
    setAssigned(prev => { const next = new Set(prev); if (next.has(noteId)) next.delete(noteId); else next.add(noteId); return next })
  }

  async function handleDeleteNote(n) {
    if (!(await confirmDialog({ title: 'Delete note block?', message: `Delete "${n.name}"? It will stop printing everywhere it's assigned, not just this sheet.`, confirmLabel: 'Delete', danger: true }))) return
    try { await deleteNoteSticker(n.id); reload(); addToast && addToast('Note block deleted') }
    catch { addToast && addToast('Delete failed', 'error') }
  }

  async function handleSave() {
    if (!frame || !vis) { setErr('Load a sample sheet first.'); return }
    setSaving(true); setErr('')
    try {
      const layout = { ...makeDefaultLayout(), rect }
      const sizeName = `${(vis.vw / 72).toFixed(1)}″ × ${(vis.vh / 72).toFixed(1)}″`
      const payload = { name: sizeName, vis_w: vis.vw, vis_h: vis.vh, page_frame: frame, layout }
      const saved = matchedProfileId ? await updateTitleblockProfile(matchedProfileId, payload) : await saveTitleblockProfile(payload)
      const profileId = saved.id
      setMatchedProfileId(profileId)

      await Promise.all((notes || []).map(async note => {
        const wasAssigned = (note.target_sheet_templates || []).includes(templateId)
        const isAssigned = assigned.has(note.id)
        const patch = {}
        let dirty = false
        if (wasAssigned !== isAssigned) {
          const targets = new Set(note.target_sheet_templates || [])
          if (isAssigned) targets.add(templateId); else targets.delete(templateId)
          patch.target_sheet_templates = Array.from(targets)
          dirty = true
        }
        if (isAssigned) {
          const curPos = note.positions || {}
          const newPos = notePos[note.id]
          const existing = curPos[profileId]
          if (newPos && (!existing || existing.x !== newPos.x || existing.y !== newPos.y)) {
            patch.positions = { ...curPos, [profileId]: newPos }
            dirty = true
          }
        }
        if (dirty) await updateNoteSticker(note.id, patch)
      }))

      addToast && addToast('Saved')
      reload()
    } catch (e) { setErr(e.message || 'Save failed') } finally { setSaving(false) }
  }

  function zoomBy(f) {
    const cont = containerRef.current; if (!cont || !pageBitmap) return
    const mx = cont.clientWidth / 2, my = cont.clientHeight / 2
    const v = vpRef.current
    const [cx, cy] = [(mx - v.x) / v.z, (my - v.y) / v.z]
    const nz = clamp(v.z * f, 0.05, 40)
    vpRef.current = { z: nz, x: mx - cx * nz, y: my - cy * nz }
    redraw()
  }

  if (editingNote) {
    return (
      <NoteEditor
        initial={editingNote === 'new' ? null : editingNote}
        presetTargetTemplateId={editingNote === 'new' ? templateId : null}
        onCancel={() => setEditingNote(null)}
        onSaved={() => { setEditingNote(null); reload(); addToast && addToast('Note block saved') }}
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 24px', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 }}>
        <div style={{ flex: 1, fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-mid)' }}>
          Titleblock overlay for <strong style={{ color: 'var(--aj-text)' }}>{templateName}</strong>
        </div>
        <button onClick={handleSave} disabled={saving || !pageBitmap} style={{ ...s.btnPrimary, opacity: (saving || !pageBitmap) ? 0.5 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
      </div>

      <div style={{ display: 'flex', gap: 0, flex: 1, minHeight: 0 }}>
        <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 18px', borderRight: '0.5px solid var(--aj-border)', overflowY: 'auto' }}>
          {loading && <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', fontFamily: 'DM Sans, sans-serif' }}>Loading sample sheet…</div>}
          {noSample && !loading && (
            <div style={{ fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-mid)', lineHeight: 1.5 }}>
              No existing sheets of this template have a PDF yet. Pick any real project sheet the same size to calibrate against.
            </div>
          )}
          {!noSample && !loading && pageBitmap && (
            manualPickerOpen
              ? <button onClick={() => setManualPickerOpen(false)} style={s.link}>Use the auto-loaded sample</button>
              : <button onClick={() => setManualPickerOpen(true)} style={s.link}>Calibrate against a different sheet</button>
          )}

          {manualPickerOpen && (
            <>
              <div>
                <label style={s.label}>Project</label>
                <select style={s.input} value={projectId} onChange={e => setProjectId(e.target.value)}>
                  <option value="">Select a project…</option>
                  {(projects || []).map(p => (
                    <option key={p.id} value={p.id}>{[getFirst(p.fields['Project Number']), getFirst(p.fields['Project Name'])].filter(Boolean).join(' — ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={s.label}>Sheet</label>
                <select style={s.input} value={sheetId} onChange={e => onPickSheet(e.target.value)} disabled={!projectId || sheets === null}>
                  <option value="">{!projectId ? 'Pick a project first' : sheets === null ? 'Loading…' : (sheets.length ? 'Select a sheet…' : 'No sheets with PDFs')}</option>
                  {(sheets || []).map(sh => (
                    <option key={sh.id} value={sh.id}>{getFirst(sh.fields['Sheet Number and Title']) || getFirst(sh.fields['Sheet Number']) || sh.id}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {vis && (
            <div style={{ fontSize: 11, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)', lineHeight: 1.6 }}>
              Sheet size: {(vis.vw / 72).toFixed(1)}″ × {(vis.vh / 72).toFixed(1)}″<br />
              <span style={{ color: 'var(--aj-text-muted)' }}>
                {matchedProfileId
                  ? 'Already calibrated for this size — shared with every other template of the same size.'
                  : 'Not calibrated yet for this size. Drag the green box into place and Save — it will apply to every sheet template that shares this size.'}
              </span>
            </div>
          )}

          {pageBitmap && (
            <div>
              <label style={s.label}>Note blocks on this sheet</label>
              {notes === null ? (
                <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', fontFamily: 'DM Sans, sans-serif' }}>Loading…</div>
              ) : notes.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', fontFamily: 'DM Sans, sans-serif' }}>No note blocks yet.</div>
              ) : notes.map(n => (
                <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={assigned.has(n.id)} onChange={() => toggleAssigned(n.id)} />
                    <span style={{ color: '#1a6fa8' }}>▚</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.name}</span>
                  </label>
                  <button onClick={() => setEditingNote(n)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>Edit</button>
                  <button onClick={() => handleDeleteNote(n)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 13, padding: '0 2px', flexShrink: 0 }} title="Delete">✕</button>
                </div>
              ))}
              <button onClick={() => setEditingNote('new')} style={{ ...s.btnSecondary, marginTop: 8, width: '100%' }}>+ New note block</button>
            </div>
          )}

          <div style={{ fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-mid)', lineHeight: 1.5, marginTop: 4 }}>
            <strong>Scroll</strong> to zoom, <strong>drag empty space</strong> to pan. Drag the <span style={{ color: '#1D9E75' }}>green box</span> to reposition the issuance block; drag its corner to resize. Check a note block to place it, then drag its blue box into position.
          </div>
          {err && <div style={{ fontSize: 12, color: '#c0392b', fontFamily: 'DM Sans, sans-serif' }}>{err}</div>}
        </div>

        <div style={{ flex: 1, minWidth: 0, position: 'relative', background: 'var(--aj-bg-muted)' }}>
          <div ref={containerRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
            <canvas
              ref={canvasRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              style={{ display: 'block', touchAction: 'none', cursor: pageBitmap ? 'crosshair' : 'default' }}
            />
          </div>
          {!pageBitmap && !loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--aj-text-muted)', fontSize: 13, fontFamily: 'DM Sans, sans-serif', pointerEvents: 'none' }}>
              Pick a project sheet to begin.
            </div>
          )}
          {pageBitmap && (
            <div style={{ position: 'absolute', right: 12, bottom: 12, display: 'flex', gap: 6 }}>
              <button onClick={() => zoomBy(1 / 1.3)} style={s.btnSecondary} title="Zoom out">−</button>
              <button onClick={() => zoomBy(1.3)} style={s.btnSecondary} title="Zoom in">+</button>
              <button onClick={fitView} style={s.btnSecondary} title="Fit to view">Fit</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Note-block content editor: name, width, template body. Which sheets it prints on is managed
// from the sheet-template panel above (checkbox list) — not here — so a note is never edited in
// two disconnected places for the same fact. ─────────────────────────────────────────────────────
function NoteEditor({ initial, presetTargetTemplateId, onSaved, onCancel }) {
  const [name, setName] = useState(initial?.name || '')
  const [widthIn, setWidthIn] = useState(initial?.real_width_in != null ? initial.real_width_in : 4.5)
  const [html, setHtml] = useState(initial?.template_html || '')
  const [css, setCss] = useState(initial?.template_css || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  function loadStarter() { if (!name.trim()) setName('Site Plan Notes'); setHtml(siteplanNotesStarterHtml); setCss(siteplanNotesStarterCss) }

  async function save() {
    if (!name.trim()) { setErr('Give the note block a name.'); return }
    setSaving(true); setErr('')
    try {
      const payload = { name: name.trim(), template_html: html, template_css: css, real_width_in: Number(widthIn) || 4.5, data_source: 'parcel' }
      if (!initial?.id && presetTargetTemplateId) payload.target_sheet_templates = [presetTargetTemplateId]
      const saved = initial?.id ? await updateNoteSticker(initial.id, payload) : await saveNoteSticker(payload)
      onSaved(saved)
    } catch (e) { setErr(e.message || 'Save failed'); setSaving(false) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 24px', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 }}>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif', padding: '4px 8px 4px 0' }}>← Back</button>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)' }}>{initial?.id ? 'Edit note block' : 'New note block'}</div>
        <button onClick={save} disabled={saving} style={{ ...s.btnPrimary, opacity: saving ? 0.5 : 1 }}>{saving ? 'Saving…' : 'Save note block'}</button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 240px' }}>
            <label style={s.label}>Note block name</label>
            <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="Site Plan Notes" />
          </div>
          <div style={{ width: 120 }}>
            <label style={s.label}>Width (in)</label>
            <input style={s.input} type="number" step="0.25" value={widthIn} onChange={e => setWidthIn(e.target.value)} />
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <label style={{ ...s.label, marginBottom: 0 }}>Template</label>
            <button onClick={loadStarter} style={{ ...s.btnSecondary, fontSize: 11, padding: '3px 8px' }}>Load Site Plan Notes starter</button>
          </div>
          <textarea style={{ ...s.input, minHeight: 160, fontFamily: 'DM Mono, monospace', fontSize: 11, resize: 'vertical' }} value={html} onChange={e => setHtml(e.target.value)} placeholder="Note template HTML (Handlebars). Use the starter, then tweak." />
        </div>
        <div>
          <label style={s.label}>Template styles (CSS)</label>
          <textarea style={{ ...s.input, minHeight: 90, fontFamily: 'DM Mono, monospace', fontSize: 11, resize: 'vertical' }} value={css} onChange={e => setCss(e.target.value)} />
        </div>
        {err && <div style={{ fontSize: 12, color: '#c0392b', fontFamily: 'DM Sans, sans-serif' }}>{err}</div>}
        <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', fontFamily: 'DM Sans, sans-serif', lineHeight: 1.5 }}>
          Which sheets this prints on, and where it sits, is set from the Titleblock tab of each sheet template — not here.
        </div>
      </div>
    </div>
  )
}
